import { Typography, Grid, Chip, Stack } from "@mui/material";
import { useLocalTargetState } from "../util/useLocalTargetState";
import { useLocalSolutionMapState } from "../util/useLocalSolutionMapState";
import { useLocalBaselineEmissionsState } from "../util/useLocalBaselineEmissionsState";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { analyzePlan } from "../util/planAnalysis/analyzePlan.ts";
import { LoadingIndicator } from "../components/LoadingIndicator.tsx";
import { EventCategory } from "@labs/shared/types/EventCategory.ts";
import PlanBySourceChart from "../charts/PlanBySourceChart.tsx";
import { LabsCard } from "../components/LabsCard.tsx";
import { useLocalUserState } from "../util/useLocalUserState.ts";
import { generateSolutionCopyByScope } from "@labs/shared/util/generateSolutionCopyByScope.ts";
import { Scope } from "@labs/shared/types/Solution.ts";
import { SummaryList } from "../components/SummaryList.tsx";
import { generateEmailTemplateData } from "@labs/email-lambda/util/generateEmailTemplateData.ts";
import { emailTheme } from "@labs/email-lambda/util/emailTheme.ts";
import { CostScoreDisplay } from "../components/CostScoreDisplay.tsx";
import { StatBoxRow } from "../components/StatBoxRow.tsx";
import { useTheme } from "@mui/material/styles";
import { blueGrey, orange, green } from "@mui/material/colors";
import SearchIcon from "@mui/icons-material/Search";
import CheckIcon from "@mui/icons-material/Check";
import { IconBox } from "../components/IconBox.tsx";
import { Outlet } from "react-router";

const ForecastLineChart = lazy(() => import("../charts/ForecastLineChart"));
const GapWaterfallChart = lazy(() => import("../charts/GapWaterfallChart"));

const Dashboard = () => {
  const theme = useTheme();
  const [target] = useLocalTargetState();
  const [solutionMap] = useLocalSolutionMapState();
  const [baselineEmissions] = useLocalBaselineEmissionsState();
  const solutionCopyByScope = useMemo(
    () => generateSolutionCopyByScope(solutionMap),
    [solutionMap],
  );
  const [, setUser] = useLocalUserState();
  const [hoveredCharts] = useState(new Set<string>());
  const analyzedPlan = useMemo(
    () => analyzePlan(solutionMap, target, baselineEmissions),
    [baselineEmissions, solutionMap, target],
  );

  const plannedReduction = -Math.round(analyzedPlan.planAbsoluteImpact);
  const plannedImpactDescription =
    plannedReduction > 0 ? "Absolute Reduction" : "Absolute Increase";

  // todo: rename
  const emailTemplateData = generateEmailTemplateData(
    solutionMap,
    baselineEmissions,
    target,
    analyzedPlan,
    // todo make these urls optional?
    "",
    "",
    "",
    "",
    emailTheme,
  );

  // assume that if the user has landed on the dashboard, their "first pass" at
  // making a plan is complete
  useEffect(() => {
    setUser((prevUser) => ({ ...prevUser, firstPassComplete: true }));
  }, [setUser]);

  const statBoxItems = [
    {
      label: "Target Year",
      value: target.targetYear,
      description: `Base year ${target.baselineYear}`,
    },
    {
      label: "Goal",
      value: `${target.goal}%`,
      description: "Absolute Reduction",
    },
    {
      label: "Planned",
      value: `${Math.abs(plannedReduction)}%`,
      description: plannedImpactDescription,
    },
    {
      label: "Investment",
      value: <CostScoreDisplay costScore={analyzedPlan.costScore} />,
      description: "Marginal Abatement Cost",
    },
  ];

  return (
    <Grid container spacing={4} sx={{ alignItems: "flex-start" }}>
      <Grid size={{ xs: 12, md: 7 }}>
        <LabsCard>
          <Typography variant={"h4"} color={"primary.dark"}>
            Summary
          </Typography>
          <Typography variant={"body1"}>
            {emailTemplateData.topLevelSummary}
          </Typography>
          <Typography variant={"body1"} color={"primary.dark"}>
            Emissions reduction plan
          </Typography>
          <Suspense fallback={<LoadingIndicator />}>
            <GapWaterfallChart
              target={target}
              analyzedPlan={analyzedPlan}
              showEditLink
              hoveredCharts={hoveredCharts}
              analyticsCategory={EventCategory.Dashboard}
            />
          </Suspense>
          <StatBoxRow items={statBoxItems} />
          <Typography variant={"body1"} color={"primary.dark"}>
            Emissions by source
          </Typography>
          <Suspense fallback={<LoadingIndicator />}>
            <PlanBySourceChart
              analyzedPlan={analyzedPlan}
              showEditLink={false}
              hoveredCharts={hoveredCharts}
              analyticsCategory={EventCategory.Plan}
            />
          </Suspense>
        </LabsCard>
      </Grid>
      <Grid size={{ xs: 12, md: 5 }}>
        <Outlet
          context={{ analyzedPlan, planOnTrack: emailTemplateData.planOnTrack }}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 7 }}>
        <LabsCard bodySpacing={4}>
          <Typography variant={"h4"} color={"primary.dark"}>
            Your plan
          </Typography>
          <Typography variant={"body1"} color={"primary.dark"}>
            {target.targetYear} Target
          </Typography>
          <Suspense fallback={<LoadingIndicator />}>
            <ForecastLineChart
              hoveredCharts={hoveredCharts}
              analyticsCategory={EventCategory.Dashboard}
              showEditLink
              analyzedPlan={analyzedPlan}
              target={target}
            />
          </Suspense>
          <Chip
            label={
              <Typography
                variant={"body1"}
                sx={{
                  whiteSpace: "normal",
                  maxWidth: "100%",
                  overflowWrap: "break-word",
                }}
              >
                Your plan is{" "}
                <strong>
                  {emailTemplateData.planOnTrack ? "on track" : "off track"}
                </strong>{" "}
                to achieve your target.
              </Typography>
            }
            sx={{
              alignSelf: "center",
              padding: 1,
              paddingTop: { xs: 4, md: 0 },
              paddingBottom: { xs: 4, md: 0 },
              backgroundColor: emailTemplateData.planOnTrack
                ? green[100]
                : orange[100],
            }}
          />
          <Stack direction={"column"} spacing={2}>
            <Stack direction={"row"} spacing={1}>
              <IconBox borderColor={blueGrey[600]}>
                <SearchIcon sx={{ color: blueGrey[600] }} />
              </IconBox>
              <Typography variant={"h5"}>Your emissions</Typography>
            </Stack>
            <SummaryList
              textContent={[
                {
                  title: "Baseline + Business Growth",
                  borderColor: blueGrey[600],
                  listItems: [emailTemplateData.businessGrowthSummary],
                },
                {
                  title: "Remaining Emissions",
                  borderColor: blueGrey[600],
                  listItems: [emailTemplateData.remainingEmissionsSummary],
                },
              ]}
            />
          </Stack>

          <Stack direction={"column"} spacing={2}>
            <Stack direction={"row"} spacing={1}>
              <IconBox borderColor={"primary.dark"}>
                <CheckIcon sx={{ color: "primary.dark" }} />
              </IconBox>
              <Typography variant={"h5"} color={"primary.dark"}>
                Solutions identified
              </Typography>
            </Stack>
            <Grid container spacing={4}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Stack direction={"column"} spacing={1}>
                  <Typography variant={"h6"} color={"primary.dark"}>
                    Scope 1
                  </Typography>
                  <SummaryList
                    textContent={[
                      {
                        title: "Cut energy waste",
                        borderColor: theme.palette.primary.dark,
                        listItems:
                          solutionCopyByScope[Scope.Scope1].cutEnergyWaste,
                      },
                      {
                        title: "Electrify",
                        borderColor: theme.palette.primary.dark,
                        listItems: solutionCopyByScope[Scope.Scope1].electrify,
                      },
                      {
                        title: "Buy clean",
                        borderColor: theme.palette.primary.dark,
                        listItems: solutionCopyByScope[Scope.Scope1].buyClean,
                      },
                      {
                        title: "Less common solutions",
                        borderColor: theme.palette.primary.dark,
                        listItems:
                          solutionCopyByScope[Scope.Scope1].lessCommonSolutions,
                      },
                    ]}
                  />
                </Stack>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Stack direction={"column"} spacing={1}>
                  <Typography variant={"h6"} color={"primary.dark"}>
                    Scope 2
                  </Typography>
                  <SummaryList
                    textContent={[
                      {
                        title: "Cut energy waste",
                        borderColor: theme.palette.primary.dark,
                        listItems:
                          solutionCopyByScope[Scope.Scope2].cutEnergyWaste,
                      },
                      {
                        title: "Buy clean",
                        borderColor: theme.palette.primary.dark,
                        listItems: solutionCopyByScope[Scope.Scope2].buyClean,
                      },
                    ]}
                  />
                </Stack>
              </Grid>
            </Grid>
          </Stack>
        </LabsCard>
      </Grid>
    </Grid>
  );
};

export default Dashboard;
